'use client'

import { type SetStateAction, type Dispatch, useState } from 'react'

import type { ProductFragment } from '@/lib/types/datocms/graphql/generated'
import FeaturesContentSection from '@/components/sections/FeaturesContentSection'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import CtaLarge from '@/components/ui/cta/CtaLarge'
import ProductGallery from './ProductGallery'
import { Overview } from '@/components/product/Overview'
import { Specifications } from '@/components/product/Specifications'
import ProductDetailAccordion from './ProductDetailAccordion'
import { StructuredText } from 'react-datocms'
import { useIsInternationalSubcategory } from '@/hooks/useIsInternationalSubcategory'
import Image from 'next/image'

export default function ProductDetails({
	product,
	setIsImageModalOpen,
	isImageModalOpen,
	setShowMobileMenu,
	setIsMegaMenuOpen,
}: {
	product: ProductFragment
	setIsImageModalOpen: Dispatch<SetStateAction<boolean>>
	isImageModalOpen: boolean
	setShowMobileMenu: Dispatch<SetStateAction<boolean>>
	setIsMegaMenuOpen: Dispatch<SetStateAction<boolean>>
}) {
	const [activeAccordion, setActiveAccordion] = useState<string | null>(null)
	const { isInternational } = useIsInternationalSubcategory(product.category?.id as string)

	const handleAccordionClick = (id: string) => {
		if (activeAccordion === id) {
			setActiveAccordion(null)
		} else {
			setActiveAccordion(id)
		}
	}

	return (
		product && (
			// Product Details
			// Mobile: Slide 3
			<div className="product-details w-full grid grid-cols-9 h-[calc(100dvh-140px)] overflow-y-auto @4xl:overflow-y-hidden">
				{/* Left Column */}
				<div className="col-span-9 @4xl:col-span-4 @6xl:col-span-4 flex flex-col px-3 py-6 @2xl:p-10 border-r h-full  @4xl:overflow-y-auto">
					<h2 className="text-2xl font-medium mb-4">{product.name}</h2>

					{/* Mobile Nav: Primary Image */}
					<div className="md:hidden relative w-full mb-4 rounded-lg overflow-hidden bg-white-frosted aspect-[4/3]">
						<Image
							src={product.coverImage?.[0]?.url as string}
							alt={(product.coverImage?.[0]?.alt as string) ?? 'Product Image'}
							width={150}
							height={150}
							className="w-full h-full object-cover"
						/>
					</div>

					<div className="text-base text-blue-dark">
						<StructuredText data={product.productDescription} />
					</div>

					<CtaLarge
						cta={{
							__typename: 'NavigationItemRecord',
							id: '1',
							label: 'Learn more about this model',
							internalLink: product,
							useExternalLink: false,
							openInNewWindow: false,
						}}
						icon={{
							icon: 'Chevron',
							orientation: 'right',
							size: 'sm',
						}}
						isInternational={isInternational}
						className="w-full mt-10 mb-4"
						onClick={() => {
							setShowMobileMenu(false)
							setIsMegaMenuOpen(false)
						}}
						closeMegaMenu
					/>

					{product.cta && (
						<CtaLarge
							cta={product.cta}
							icon={{
								icon: 'ClipboardLines',
								orientation: 'left',
							}}
							className="w-full mb-10"
							variant="secondary"
						/>
					)}

					{/* Overview Accordion */}
					{product.overview?.find(
						(section) =>
							section.__typename === 'FeaturesContentSectionRecord' ||
							section.__typename === 'ProductContentRecord'
					) ? (
						<ProductDetailAccordion
							idx="1"
							title="Overview"
							activeAccordion={activeAccordion}
							handleAccordionClick={handleAccordionClick}
							content={<Overview overview={product.overview} inMegaMenu />}
							className="!mt-10"
						/>
					) : null}

					{/* Variations & Specifications Accordion */}
					{product.specifications?.find(
						(section) =>
							section.__typename === 'FeaturesContentSectionRecord' ||
							section.__typename === 'ProductContentRecord'
					) ? (
						<ProductDetailAccordion
							idx="2"
							title="Specifications"
							activeAccordion={activeAccordion}
							handleAccordionClick={handleAccordionClick}
							content={<Specifications specifications={product.specifications} inMegaMenu />}
						/>
					) : null}

					{/* Possible Headramp Combinations Accordion */}
					{product.possibleHeadrampCombinations &&
						product.possibleHeadrampCombinations.length > 0 && (
							<ProductDetailAccordion
								idx="3"
								title="Possible Headramp Combinations"
								activeAccordion={activeAccordion}
								handleAccordionClick={handleAccordionClick}
								content={
									<div className="flex flex-col gap-2">
										{product.possibleHeadrampCombinations.map((combination) => (
											<CtaLarge
												key={combination.id as string}
												variant="secondary"
												cta={{
													__typename: 'NavigationItemRecord',
													id: combination.id,
													label: combination.name,
													internalLink: {
														__typename: 'ProductRecord',
														id: combination.id,
														slug: combination.slug,
													},
													useExternalLink: false,
													openInNewWindow: false,
												}}
												icon={{
													icon: 'Chevron',
													orientation: 'right',
													size: 'sm',
												}}
												className="w-full justify-between"
												onClick={() => {
													setShowMobileMenu(false)
													setIsMegaMenuOpen(false)
												}}
												closeMegaMenu
											/>
										))}
									</div>
								}
							/>
						)}

					{/* Possible Trailer Combinations Accordion */}
					{product.possibleTrailerCombinations &&
						product.possibleTrailerCombinations.length > 0 && (
							<ProductDetailAccordion
								idx="4"
								title="Possible Trailer Combinations"
								activeAccordion={activeAccordion}
								handleAccordionClick={handleAccordionClick}
								content={
									<div className="flex flex-col gap-2">
										{product.possibleTrailerCombinations.map((combination) => (
											<CtaLarge
												key={combination.id as string}
												variant="secondary"
												cta={{
													__typename: 'NavigationItemRecord',
													id: combination.id,
													label: combination.name,
													internalLink: {
														__typename: 'ProductRecord',
														id: combination.id,
														slug: combination.slug,
													},
													useExternalLink: false,
													openInNewWindow: false,
												}}
												icon={{
													icon: 'Chevron',
													orientation: 'right',
													size: 'sm',
												}}
												className="w-full justify-between"
												closeMegaMenu
												onClick={() => {
													setShowMobileMenu(false)
													setIsMegaMenuOpen(false)
												}}
											/>
										))}
									</div>
								}
							/>
						)}
				</div>

				{/* Right Column (Product Gallery, Highlights & Upgrades) */}
				<div className="relative col-span-9 @4xl:col-span-5 @6xl:col-span-5 @4xl:overflow-scroll">
					<ProductGallery
						product={product}
						setIsImageModalOpen={setIsImageModalOpen}
						isImageModalOpen={isImageModalOpen}
					/>

					{/* Highlights & Upgrades Tabs */}
					<TabGroup className="px-3 @2xl:px-10 my-5 border-t">
						<TabList className="border-b">
							<Tab className="px-8 py-4 -mb-px data-[selected]:border-b-2 data-[selected]:border-blue data-[selected]:text-blue">
								Standard Features
							</Tab>
							<Tab className="px-8 py-4 -mb-px data-[selected]:border-b-2 data-[selected]:border-blue data-[selected]:text-blue">
								Upgrades
							</Tab>
						</TabList>

						<TabPanels>
							<div className="mt-4">
								{product.highlights && (
									<TabPanel>
										<FeaturesContentSection inMegaMenu {...product.highlights} />
									</TabPanel>
								)}
								{product.upgrades && (
									<TabPanel>
										<FeaturesContentSection inMegaMenu {...product.upgrades} />
									</TabPanel>
								)}
							</div>
						</TabPanels>
					</TabGroup>
				</div>
			</div>
		)
	)
}
